import React, { useEffect, useState } from 'react';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { IS_MKOSINT_TENANT } from '../../../actions/configuration';
import idConstants from '../../../data/idConstants';

import {
  getOrdersListing,
  getOrderHistory
} from '../../../actions/api/OrdersAction';
import { getOrderSummary } from '../../../actions/api/OrderSummaryAction';
import { orderStatus as orderStatusUtils } from '../../../shared/shared-function/helperFunctions';
import {
  ITEM_LENGTH_ORDER_HISTORY,
  INTERNAL_SELLER_ORDER_ROW_DATA
} from '../../../shared/constants/field-constants';
import {
  Search,
  Table,
  Tabs,
  EmptyPage,
  Filter
} from '../../../msreact-kit/packages/html-components';
import SidebarContentWrapper from '../../BaseLayout/SidebarContainer';
import Loader from '../../../components/Shared/Utils/Loader';
import Footer from '../../BaseLayout/footer';
import { pageLimit, accessRoles } from '../../../data/appConstants';
import useHandleLoader from '../../../msreact-kit/hooks/useHandleLoader';
import CustomToolTip from '../../Shared/custom-tooltip';
import {
  handleDownloadInvoice,
  getInvoiceInfoIcons
} from '../order-summary-page/utils';
import { Tooltip, Dialog } from '@mobilitystore/components-interface';
import './index.scss';

const OrderCard = ({ isHistory = false, isListing = false }) => {
  const history = useHistory();
  const location = useLocation();
  // Restore tab/pageSize/search/filters from the URL so returning via breadcrumb keeps the user's view
  const isPersistedView = isListing || isHistory;
  const initialQueryParams = new URLSearchParams(location.search);
  const queryPageSize = Number(initialQueryParams.get('pageSize'));
  const initialStatus =
    isListing && initialQueryParams.get('tab') === orderStatusUtils.ONGOING_ORDER
      ? orderStatusUtils.ONGOING_ORDER
      : orderStatusUtils.NEW;
  const initialPageSize =
    isPersistedView && queryPageSize > 0 ? queryPageSize : ITEM_LENGTH_ORDER_HISTORY;
  const initialSearch = isPersistedView ? initialQueryParams.get('search') || '' : '';
  const initialFilterId = isPersistedView
    ? initialQueryParams.get('filters')?.split(',').filter(Boolean) || []
    : [];

  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState(initialSearch);
  const [tableData, setTableData] = useState([]);
  const [filterId, setFilterId] = useState(initialFilterId);
  const [categories, setCategories] = useState();
  const [currentPage, setCurrentPage] = useState(pageLimit.INITIAL_PAGE_COUNT);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [paginationData, setPaginationData] = useState(0);
  const [showInvoiceInfo, setShowInvoiceInfo] = useState(false);
  const [showTooltipState, setShowTooltipState] = useState({
    showTooltipInfo: false,
    productOrderDetailId: ''
  });

  let orderStatus = '';
  const dispatch = useDispatch();
  const [handleLoader] = useHandleLoader();

  const { loader, orderList, productCategories, content, user, invoiceInfo } =
    useSelector((state) => ({
      content: state.ContentReducer.content,
      loader: state.LoaderReducer.loader,
      productCategories: state.MasterDataReducer.productCategories,
      user: state.loginReducer.user,
      orderList: isListing
        ? state.OrdersReducer.orderListing
        : state.OrdersReducer.orderHistory,
      invoiceInfo: state.OrderSummaryReducer.invoiceInfo
    }));

  const userLoginRole = user?.roles?.[0];
  const isCSM =
    user?.roles?.includes(accessRoles.csm) && userLoginRole === accessRoles.csm;

  if (IS_MKOSINT_TENANT) {
    orderStatus = idConstants.order.statusId.subscribed.name;
  } else if (isCSM) {
    orderStatus = orderStatusUtils.CSM_ORDER_HISTORY;
  } else if (isHistory) {
    orderStatus = orderStatusUtils.ORDER_HISTORY;
  }

  const OrderHistoryColDefContent =
    content?.components?.order_history?.table?.column?.headings;

  const rowData = OrderHistoryColDefContent?.filter((item) => {
    return item.roles.length !== 0 && item.roles?.includes(userLoginRole);
  });
  const orderRowData = IS_MKOSINT_TENANT ? INTERNAL_SELLER_ORDER_ROW_DATA : rowData;

  const onSearch = (value) => {
    setSearch(value);
    setCurrentPage(pageLimit.INITIAL_PAGE_COUNT);
    updateViewStateParams({ search: value });
  };

  // Keeps ?tab=/?pageSize=/?search=/?filters= in sync so breadcrumb/back navigation can restore the view
  const updateViewStateParams = (updates) => {
    if (!isPersistedView) return;
    const params = new URLSearchParams(history.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    history.replace({
      pathname: history.location.pathname,
      search: params.toString() ? `?${params.toString()}` : ''
    });
  };

  const handleChange = (item) => {
    setStatus(item.id);
    setCurrentPage(pageLimit.INITIAL_PAGE_COUNT);
    updateViewStateParams({ tab: item.id });
  };

  useEffect(() => {
    handleLoader();
  }, []);

  useEffect(() => {
    // Only show the popup for string-type invoiceInfo (non-uzos orders)
    // Uzos orders return a full data object; their invoice is shown in the order detail page
    if (invoiceInfo && typeof invoiceInfo === 'string') setShowInvoiceInfo(true);
  }, [invoiceInfo]);

  useEffect(() => {
    const paginationValues = {
      totalPages: orderList?.filter?.totalPages
    };
    setPaginationData(paginationValues);
    const filterData = orderList?.filter?.categories?.map((item) => {
      const categoryItem = productCategories.find(
        (element) => element.categoryId === item.id
      );
      return {
        label: categoryItem?.displayName,
        id: categoryItem?.categoryId,
        isSelected: filterId.includes(categoryItem?.categoryId),
        name: categoryItem?.categoryId
      };
    });
    setCategories(filterData);
  }, [orderList, productCategories, filterId]);

  const handleViewMore = (productOrderDetailId, dcsOrderId) => {
    history.push({
      pathname: `/dashboard/order-management/order-details/${productOrderDetailId}`,
      state: {
        isHistory,
        dcsOrderId: dcsOrderId,
        returnSearch: isPersistedView ? location.search : ''
      }
    });
  };

  const clearInvoiceInfo = () => {
    dispatch({
      type: 'GET_INVOICE_INFO',
      payload: ''
    });
    setShowInvoiceInfo(false);
  };

  const handleDownloadInvoiceDoc = (
    dispatch,
    productOrderDetailId,
    productOrderId
  ) => {
    dispatch(getOrderSummary(user, productOrderDetailId)).then((res) => {
      if (res?.orderSubscriptionMap?.tenantId) {
        dispatch({
          type: 'GET_INVOICE_INFO',
          payload: ''
        });
        handleDownloadInvoice(
          dispatch,
          productOrderId,
          res?.orderSubscriptionMap?.tenantId
        );
      }
    });
  };

  const toggleTooltipInfo = (val, element) => {
    setShowTooltipState({
      ...showTooltipState,
      showTooltipInfo: val,
      productOrderDetailId: element?.productOrderDetailId
    });
  };

  useEffect(() => {
    const formatedTableData = [];
    const formatedCSMOrderData = [];
    orderList?.content?.length > 0 &&
      orderList?.content?.forEach((element) => {
        const formatedData = {
          orderId: element?.dcsOrderId,
          productOrderDetailId: element?.productOrderDetailId,
          productName: element?.productDetailsDto?.displayName,
          productCategory: (
            <CustomToolTip
              id={element?.productDetailsDto?.category?.displayName}
              tooltipData={element?.productDetailsDto?.category?.displayName}
              width='dynamic'
              className='asset-type__content'
            >
              <p
                className='content_style text-ellipsis'
                id={element?.productDetailsDto?.category?.displayName}
              >
                {element?.productDetailsDto?.category?.displayName}
              </p>
            </CustomToolTip>
          ),
          buyerName: [element?.orderCreatedBy?.userFirstName, element?.orderCreatedBy?.userLastName].filter(Boolean).join(' '),
          orderedDate: moment(element?.orderDate).format('DD/MM/YYYY, hh:mm A'),
          quantity: element?.orderedQuantity,
          availableStocks: element?.productDetailsDto?.quantity?.quantity ?? 'NA',
          viewMore: (
            <a
              className='ms-pointer'
              onClick={() =>
                handleViewMore(element?.productOrderDetailId, element?.dcsOrderId)
              }
              tabIndex={0}
              role='button'
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  handleViewMore(element?.productOrderDetailId, element?.dcsOrderId);
              }}
            >
              {i18next.t('ORDER_MANAGEMENT.ORDER_CARD.VIEW_DETAILS.VIEW_MORE')}
            </a>
          )
        };
        formatedTableData.push(formatedData);
      });
    orderList?.content?.forEach((element) => {
      const formatedOrderData = {
        orderId: element?.dcsOrderId,
        productOrderDetailId: element?.productOrderDetailId,
        productName: element?.productDetailsDto?.displayName,
        companyName: element?.orderCreatedBy?.companyName,
        customerName: [element?.orderCreatedBy?.userFirstName, element?.orderCreatedBy?.userLastName].filter(Boolean).join(' '),
        orderedDate: moment(element?.orderDate).format('DD/MM/YYYY, hh:mm A'),
        orderStatus: element?.orderStatus,
        paymentStatus: element?.invoicePaymentStatus,
        viewMore: (
          <Tooltip
            title={content?.components?.order_history?.invoice?.title}
            show={
              showTooltipState.showTooltipInfo &&
              showTooltipState.productOrderDetailId === element?.productOrderDetailId
            }
          >
            <a
              className='ms-pointer'
              onClick={() =>
                handleDownloadInvoiceDoc(
                  dispatch,
                  element?.productOrderDetailId,
                  element?.productOrderId
                )
              }
              tabIndex={0}
              role='button'
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  handleDownloadInvoiceDoc(
                    dispatch,
                    element?.productOrderDetailId,
                    element?.productOrderId
                  );
              }}
            >
              <span
                onTouchEnd={() => toggleTooltipInfo(false, element)}
                onTouchMove={() => toggleTooltipInfo(true, element)}
                onMouseEnter={() => toggleTooltipInfo(true, element)}
                onMouseLeave={() => toggleTooltipInfo(false, element)}
              >
                <i className={`a-icon boschicon-bosch-ic-document-arrow-down`}></i>
              </span>
            </a>
          </Tooltip>
        )
      };
      formatedCSMOrderData.push(formatedOrderData);
    });

    setTableData(isCSM ? formatedCSMOrderData : formatedTableData);
  }, [orderList, showTooltipState]);

  useEffect(() => {
    if (isListing) {
      dispatch(getOrdersListing(search, pageSize, status, currentPage, filterId));
    } else if (isHistory && user?.roles?.length > 0) {
      dispatch(
        getOrderHistory(search, pageSize, orderStatus, currentPage, filterId)
      );
    }
  }, [status, search, pageSize, currentPage, filterId, user?.roles]);

  const handlePerPage = (event) => {
    setPageSize(event.value.value);
    setCurrentPage(pageLimit.INITIAL_PAGE_COUNT);
    updateViewStateParams({ pageSize: event.value.value });
  };

  const handlePagination = (number) => {
    setCurrentPage(number);
  };

  const handleFilter = (event) => {
    const updatedFilterId = event.value
      ? [...filterId, event.name]
      : filterId.filter((id) => id !== event.name);
    setFilterId(updatedFilterId);
    updateViewStateParams({ filters: updatedFilterId.join(',') });
  };

  const tabName = [
    {
      label: i18next.t('ORDER_MANAGEMENT.ORDER_CARD.TAB_NAME.NEW_REQUEST'),
      labelCount: orderList?.filter?.statusCount
        ? orderList.filter.statusCount[orderStatusUtils.NEW]
        : 0,
      id: orderStatusUtils.NEW
    },
    {
      label: i18next.t('ORDER_MANAGEMENT.ORDER_CARD.TAB_NAME.ONGOING_ORDERS'),
      labelCount: orderList?.filter?.statusCount
        ? orderList.filter.statusCount[orderStatusUtils.ONGOING_ORDER]
        : 0,
      id: orderStatusUtils.ONGOING_ORDER
    }
  ];

  return (
    <SidebarContentWrapper>
      <div className='orders'>
        {loader && <Loader />}
        <div className='orders__container'>
          <div className='orders__container__item'>
            <div className='orders__container__item__titleBlock'>
              <h1 className='orders__container__item__heading'>
                {isListing
                  ? i18next.t('DYNAMIC_FORM.NAVIGATION_OPTIONS.MY_ORDERS')
                  : i18next.t('DYNAMIC_FORM.NAVIGATION_OPTIONS.ORDER_HISTORY')}
              </h1>
            </div>
            <div className='orders__container__item__search'>
              <Search placeholder='Search' onSearch={onSearch} value={search} />
              {/* Filter to be hidden for csm as there is only one category now for MA-IN */}
              {!isCSM && (
                <Filter
                  handleFilter={handleFilter}
                  data={categories}
                  cssName='scroll'
                />
              )}
            </div>
          </div>
          {isListing && (
            <div className='orders__container__tabs'>
              <Tabs handleChange={handleChange} tabName={tabName} activeTab={status} />
            </div>
          )}
          {orderList?.content?.length > 0 && (
            <div className='orders__container__table'>
              <Table
                rowData={orderRowData}
                data={tableData}
                handlePerPage={handlePerPage}
                handlePagination={handlePagination}
                paginationData={paginationData}
                currentPage={currentPage}
                rowsPerPage={pageSize}
              />
            </div>
          )}
          {!loader && orderList?.content?.length === 0 && (
            <div className='orders__container__empty'>
              <EmptyPage
                label=''
                disabled={false}
                showPrimaryButton={false}
                showNote={false}
                heading={i18next.t(
                  'DYNAMIC_FORM.SHARED.ERROR_MESSAGE.NO_DATA_FOUND'
                )}
              />
            </div>
          )}
          {
            <Dialog
              status='info'
              title={i18next.t('MY_ACCOUNT.INFORMATION')}
              show={showInvoiceInfo}
              showPopup={showInvoiceInfo}
              showCloseIcon={true}
              buttons={getInvoiceInfoIcons(() => clearInvoiceInfo())}
              onClose={() => clearInvoiceInfo()}
            >
              {typeof invoiceInfo === 'string' ? invoiceInfo : null}
            </Dialog>
          }
        </div>
        <Footer customClassName='d-lg-none' />
      </div>
    </SidebarContentWrapper>
  );
};

export default OrderCard;
